import {
	defer,
	EMPTY,
	from,
	Observable,
	race,
	SchedulerLike,
	timer,
} from 'rxjs';
import { catchError, exhaustMap, filter, map, take } from 'rxjs/operators';

export type TPaymentMethodSetupStatus = 'NONE' | 'PENDING' | 'FAILED';
export type TPayByBankSetupResult = 'SUCCEEDED' | 'FAILED';

const STATUS_URL = '/api/passengers/paymentMethodSetupStatus';
const VALID_STATUSES: readonly string[] = ['NONE', 'PENDING', 'FAILED'];

/** Thrown when the status endpoint returns something other than NONE/PENDING/FAILED. */
export class UnexpectedSetupStatusError extends Error {
	constructor() {
		super('Unexpected setup status response');
		this.name = 'UnexpectedSetupStatusError';
	}
}

/** Fetches the setup status once; throws on a malformed response. */
export const fetchPaymentMethodSetupStatus = async (
	get: <T>(url: string) => Promise<T>
): Promise<TPaymentMethodSetupStatus> => {
	const response = await get<{ status?: string } | undefined>(STATUS_URL);
	const status = response?.status;
	if (!status || !VALID_STATUSES.includes(status)) {
		throw new UnexpectedSetupStatusError();
	}
	return status as TPaymentMethodSetupStatus;
};

export const PAY_BY_BANK_COUNTDOWN_SECONDS = 60;
export const PAY_BY_BANK_POLL_INTERVAL_MS = 3000;
export const PAY_BY_BANK_GRACE_MS = 10000;

interface IOptions {
	intervalMs?: number;
	deadlineMs?: number;
	scheduler?: SchedulerLike;
}

/**
 * Polls the setup status until it is terminal and emits exactly one result.
 * Backend contract: PENDING is set before Stripe's webhook arrives, so NONE
 * after the redirect means success; FAILED means the setup was cancelled.
 * Rejected requests are ignored for that tick; the deadline yields FAILED.
 */
export const createPayByBankSetupPolling$ = (
	fetchStatus: () => Promise<TPaymentMethodSetupStatus>,
	{
		intervalMs = PAY_BY_BANK_POLL_INTERVAL_MS,
		deadlineMs = PAY_BY_BANK_COUNTDOWN_SECONDS * 1000 + PAY_BY_BANK_GRACE_MS,
		scheduler,
	}: IOptions = {}
): Observable<TPayByBankSetupResult> => {
	const poll$ = timer(0, intervalMs, scheduler).pipe(
		exhaustMap(() =>
			defer(() => from(fetchStatus())).pipe(catchError(() => EMPTY))
		),
		filter((status) => status !== 'PENDING'),
		map(
			(status): TPayByBankSetupResult =>
				status === 'NONE' ? 'SUCCEEDED' : 'FAILED'
		),
		take(1)
	);

	const deadline$ = timer(deadlineMs, scheduler).pipe(
		map((): TPayByBankSetupResult => 'FAILED')
	);

	return race(poll$, deadline$);
};
